import React from "react";
import { cn } from "../../DashboardPage/lib/utils";

interface SettingsPanelProps {
  /** Section title. Omit for a panel that is purely a container. */
  title?: React.ReactNode;
  /** Supporting line under the title. */
  description?: React.ReactNode;
  /** Buttons / controls aligned to the trailing edge of the header. */
  actions?: React.ReactNode;
  /**
   * Draws a hairline between the header and the body. Panels whose body starts
   * with its own bordered block (a table, a list of cards) read better without
   * it, so it is opt-in.
   */
  divided?: boolean;
  /** Marks the panel busy while its content loads. */
  loading?: boolean;
  className?: string;
  /** Applied to the body wrapper, for panels that need to lay out their own content. */
  bodyClassName?: string;
  children?: React.ReactNode;
}

/**
 * The card every Settings and Email tab sits in.
 *
 * Before this existed each tab hand-wrote the same chrome, and they had drifted
 * apart: five different max-widths, `rounded-xl` in one place and `rounded-lg`
 * everywhere else, headings with and without a description. Changing the look
 * of a tab meant editing every tab. Now the chrome lives here and a change in
 * this one file reaches all of them.
 *
 * Width is deliberately NOT set here - the tab shell owns it, so one value
 * governs every panel rather than each panel picking its own.
 */
export default function SettingsPanel({
  title,
  description,
  actions,
  divided = false,
  loading = false,
  className,
  bodyClassName,
  children,
}: SettingsPanelProps) {
  const hasHeader = Boolean(title || description || actions);

  return (
    <div
      className={cn(
        "p-4 bg-white border rounded-lg border-neutral-200 sm:p-6",
        className
      )}
      aria-busy={loading || undefined}
    >
      {hasHeader && (
        <div
          className={cn(
            // items-start, not items-center: with a description under the
            // title, centring pushes the action off the title's baseline.
            "flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between",
            divided
              ? "pb-4 mb-4 border-b border-neutral-100 sm:pb-6 sm:mb-6"
              : "mb-4 sm:mb-6"
          )}
        >
          <div className="min-w-0">
            {title && (
              <h2 className="text-base font-semibold text-black sm:text-lg">
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-1 text-sm text-neutral-600">{description}</p>
            )}
          </div>
          {actions && (
            <div className="flex flex-col gap-2 sm:flex-row sm:shrink-0">
              {actions}
            </div>
          )}
        </div>
      )}
      {children != null && <div className={bodyClassName}>{children}</div>}
    </div>
  );
}
